import { Bike, Edit, Eye, Filter, Plus, Search, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './Models.css';

const CART_VARIANT_OPTIONS = [
  { value: 'ONE_PERSON', label: '1 persona' },
  { value: 'TWO_PERSON', label: '2 personas' },
  { value: 'THREE_ADULTS_PLUS_TWO_CHILD', label: '3 personas (+2 niños sin pedaleo)' },
  { value: 'FOUR_ADULTS_PLUS_TWO_CHILD', label: '4 personas (+2 niños sin pedaleo)' },
  { value: 'SIX_ADULTS_PLUS_TWO_CHILD', label: '6 personas (+2 niños sin pedaleo)' },
  { value: 'THREE_ADULTS_PLUS_TWO_CHILD_ELECTRIC', label: '3 personas (+2) eléctrico' },
  { value: 'SIX_ADULTS_PLUS_TWO_CHILD_ELECTRIC', label: '6 personas (+2) eléctrico' },
];

const CART_VARIANT_LABELS = Object.fromEntries(
  CART_VARIANT_OPTIONS.map((o) => [o.value, o.label])
);

const Models = () => {
  const { authenticatedFetch } = useAuth();
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    categoryId: '',
    basePrice: '',
    threeToFive: '',
    additionalDay: '',
    imageUrls: [''],
    inseamOptionalEnabled: false,
    cartVariantType: '',
    cartPrice1h: '',
    cartPrice2h: '',
    cartPrice3h: '',
    cartPrice4h: '',
  });
  const [translations, setTranslations] = useState({
    es: { description: '' },
    en: { description: '' },
    de: { description: '' }
  });
  const [categories, setCategories] = useState([]);

  /** Lista admin (incluye businessGroup). Si falla, al editar aún vale editingModel.category. */
  const selectedCategoryBusinessGroup = useMemo(() => {
    const fromList = categories.find((c) => String(c.id) === String(formData.categoryId))
      ?.businessGroup;
    if (fromList != null && fromList !== '') return fromList;
    if (
      editingModel &&
      String(editingModel.categoryId) === String(formData.categoryId) &&
      editingModel.category?.businessGroup
    ) {
      return editingModel.category.businessGroup;
    }
    return undefined;
  }, [categories, formData.categoryId, editingModel]);

  const isCarritosCategory = selectedCategoryBusinessGroup === 'CARRITOS';

  const wasCarritosCategoryRef = useRef(false);
  useEffect(() => {
    if (isCarritosCategory && !wasCarritosCategoryRef.current) {
      setFormData((prev) => ({
        ...prev,
        inseamOptionalEnabled: false,
        threeToFive: '0',
        additionalDay: '0',
        basePrice: '0',
      }));
    }
    if (!isCarritosCategory && wasCarritosCategoryRef.current) {
      setFormData((prev) => ({
        ...prev,
        threeToFive:
          prev.threeToFive === '0' || prev.threeToFive === '' ? '15.0' : prev.threeToFive,
      }));
    }
    wasCarritosCategoryRef.current = isCarritosCategory;
  }, [isCarritosCategory]);

  useEffect(() => {
    fetchModels();
    fetchCategories();
  }, []);

  // Filtrar modelos cuando cambie el término de búsqueda
  useEffect(() => {
    const filtered = models.filter(model =>
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.brand.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredModels(filtered);
    setCurrentPage(1); // Resetear a la primera página cuando se filtre
  }, [models, searchTerm]);

  // Calcular modelos para la página actual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentModels = filteredModels.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredModels.length / itemsPerPage);

  const fetchModels = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando modelos desde:', `${API_BASE_URL}/api/vehicle-models`);
      
      const response = await authenticatedFetch(`${API_BASE_URL}/api/vehicle-models`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Modelos cargados:', data);
        setModels(data);
      } else {
        console.error('❌ Error al cargar modelos:', response.status, response.statusText);
        setError(`Error al cargar modelos: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      setError('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const url = buildApiUrl(API_ENDPOINTS.VEHICLE_CATEGORIES);
      const response = await authenticatedFetch(url);

      if (response.ok) {
        const data = await response.json();
        setCategories(Array.isArray(data) ? data : []);
      } else {
        console.error('❌ Error al cargar categorías:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error de conexión al cargar categorías:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUrlChange = (index, value) => {
    const newImageUrls = [...formData.imageUrls];
    newImageUrls[index] = value;
    setFormData(prev => ({
      ...prev,
      imageUrls: newImageUrls
    }));
  };

  const addImageUrl = () => {
    setFormData(prev => ({
      ...prev,
      imageUrls: [...prev.imageUrls, '']
    }));
  };

  const removeImageUrl = (index) => {
    const newImageUrls = formData.imageUrls.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      imageUrls: newImageUrls
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      description: '',
      categoryId: '',
      basePrice: '',
      threeToFive: '15.0',
      additionalDay: '',
      imageUrls: [''],
      inseamOptionalEnabled: false,
      cartVariantType: '',
      cartPrice1h: '',
      cartPrice2h: '',
      cartPrice3h: '',
      cartPrice4h: '',
    });
    setTranslations({
      es: { description: '' },
      en: { description: '' },
      de: { description: '' }
    });
    setEditingModel(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Preparar traducciones (solo incluir las que tienen descripción)
    const translationsArray = Object.entries(translations)
      .filter(([, data]) => data.description.trim() !== '')
      .map(([lang, data]) => ({
        language: lang,
        description: data.description.trim() || null
      }));
    
    const isCart = selectedCategoryBusinessGroup === 'CARRITOS';

    const submitData = {
      ...formData,
      basePrice: isCart ? 0 : parseFloat(formData.basePrice),
      threeToFive: isCart ? 0 : parseFloat(formData.threeToFive),
      additionalDay: isCart ? 0 : parseFloat(formData.additionalDay),
      imageUrls: formData.imageUrls.filter(url => url.trim() !== ''),
      inseamOptionalEnabled: isCart ? false : !!formData.inseamOptionalEnabled,
      translations: translationsArray.length > 0 ? translationsArray : undefined
    };

    if (isCart) {
      submitData.cartVariantType = formData.cartVariantType;
      submitData.cartPrice1h = parseFloat(formData.cartPrice1h);
      submitData.cartPrice2h = parseFloat(formData.cartPrice2h);
      submitData.cartPrice3h = parseFloat(formData.cartPrice3h);
      submitData.cartPrice4h = parseFloat(formData.cartPrice4h);
    } else {
      delete submitData.cartVariantType;
      delete submitData.cartPrice1h;
      delete submitData.cartPrice2h;
      delete submitData.cartPrice3h;
      delete submitData.cartPrice4h;
    }

    try {
      const url = editingModel 
        ? buildApiUrl(`${API_ENDPOINTS.VEHICLE_MODELS}/${editingModel.id}`)
        : buildApiUrl(API_ENDPOINTS.VEHICLE_MODELS);
      
      console.log('🔄 Enviando datos a:', url);
      console.log('📦 Datos:', submitData);
      
      const response = await authenticatedFetch(url, {
        method: editingModel ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Modelo guardado exitosamente:', result);
        await fetchModels();
        setShowForm(false);
        resetForm();
        alert(editingModel ? 'Modelo actualizado exitosamente' : 'Modelo creado exitosamente');
      } else {
        const errorData = await response.json();
        console.error('❌ Error del servidor:', errorData);
        
        if (response.status === 401) {
          alert('Error: No tienes permisos para realizar esta acción. Verifica tu autenticación.');
        } else if (response.status === 403) {
          alert('Error: Acceso denegado. No tienes permisos suficientes.');
        } else {
          alert(`Error: ${errorData.message || 'Error al guardar el modelo'}`);
        }
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      alert('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    }
  };

  const handleEdit = async (model) => {
    // Cargar el modelo completo con traducciones
    try {
      const response = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.VEHICLE_MODELS}/${model.id}`)
      );
      
      if (response.ok) {
        const fullModel = await response.json();
        setEditingModel(fullModel);
        
        setFormData({
          name: fullModel.name,
          brand: fullModel.brand,
          description: fullModel.description || '',
          categoryId: fullModel.categoryId.toString(),
          basePrice: fullModel.basePrice.toString(),
          threeToFive: fullModel.threeToFive.toString(),
          additionalDay: fullModel.additionalDay.toString(),
          imageUrls: fullModel.imageUrls.length > 0 ? fullModel.imageUrls : [''],
          inseamOptionalEnabled: Boolean(fullModel.inseamOptionalEnabled),
          cartVariantType: fullModel.cartVariantType || '',
          cartPrice1h: fullModel.cartPrice1h != null ? String(fullModel.cartPrice1h) : '',
          cartPrice2h: fullModel.cartPrice2h != null ? String(fullModel.cartPrice2h) : '',
          cartPrice3h: fullModel.cartPrice3h != null ? String(fullModel.cartPrice3h) : '',
          cartPrice4h: fullModel.cartPrice4h != null ? String(fullModel.cartPrice4h) : '',
        });
        
        // Cargar traducciones si existen
        const translationsData = {
          es: { description: fullModel.description || '' },
          en: { description: '' },
          de: { description: '' }
        };
        
        if (fullModel.translations && fullModel.translations.length > 0) {
          console.log('Traducciones encontradas:', fullModel.translations);
          fullModel.translations.forEach(trans => {
            if (translationsData[trans.language]) {
              translationsData[trans.language] = {
                description: trans.description !== undefined && trans.description !== null 
                  ? trans.description 
                  : translationsData[trans.language].description || ''
              };
            }
          });
        } else {
          console.log('No se encontraron traducciones, usando valores por defecto');
        }
        
        console.log('Traducciones a mostrar:', translationsData);
        setTranslations(translationsData);
        
        setShowForm(true);
      } else {
        // Si falla, usar el modelo que se pasó como parámetro
        console.warn('No se pudo cargar el modelo completo, usando datos de la lista');
        setEditingModel(model);
        setFormData({
          name: model.name,
          brand: model.brand,
          description: model.description || '',
          categoryId: model.categoryId.toString(),
          basePrice: model.basePrice.toString(),
          threeToFive: model.threeToFive.toString(),
          additionalDay: model.additionalDay.toString(),
          imageUrls: model.imageUrls.length > 0 ? model.imageUrls : [''],
          inseamOptionalEnabled: Boolean(model.inseamOptionalEnabled),
          cartVariantType: model.cartVariantType || '',
          cartPrice1h: model.cartPrice1h != null ? String(model.cartPrice1h) : '',
          cartPrice2h: model.cartPrice2h != null ? String(model.cartPrice2h) : '',
          cartPrice3h: model.cartPrice3h != null ? String(model.cartPrice3h) : '',
          cartPrice4h: model.cartPrice4h != null ? String(model.cartPrice4h) : '',
        });
        // Intentar usar traducciones de la lista si existen, sino usar valores por defecto
        const translationsData = {
          es: { description: model.description || '' },
          en: { description: '' },
          de: { description: '' }
        };
        if (model.translations && model.translations.length > 0) {
          model.translations.forEach(trans => {
            if (translationsData[trans.language]) {
              translationsData[trans.language] = {
                description: trans.description !== undefined && trans.description !== null 
                  ? trans.description 
                  : translationsData[trans.language].description || ''
              };
            }
          });
        }
        setTranslations(translationsData);
        setShowForm(true);
      }
    } catch (error) {
      console.error('Error cargando detalles del modelo:', error);
      // Si falla, usar el modelo que se pasó como parámetro
      setEditingModel(model);
      setFormData({
        name: model.name,
        brand: model.brand,
        description: model.description || '',
        categoryId: model.categoryId.toString(),
        basePrice: model.basePrice.toString(),
        threeToFive: model.threeToFive.toString(),
        additionalDay: model.additionalDay.toString(),
        imageUrls: model.imageUrls.length > 0 ? model.imageUrls : [''],
        inseamOptionalEnabled: Boolean(model.inseamOptionalEnabled),
        cartVariantType: model.cartVariantType || '',
        cartPrice1h: model.cartPrice1h != null ? String(model.cartPrice1h) : '',
        cartPrice2h: model.cartPrice2h != null ? String(model.cartPrice2h) : '',
        cartPrice3h: model.cartPrice3h != null ? String(model.cartPrice3h) : '',
        cartPrice4h: model.cartPrice4h != null ? String(model.cartPrice4h) : '',
      });
      // Intentar usar traducciones de la lista si existen, sino usar valores por defecto
      const translationsData = {
        es: { description: model.description || '' },
        en: { description: '' },
        de: { description: '' }
      };
      if (model.translations && model.translations.length > 0) {
        model.translations.forEach(trans => {
          if (translationsData[trans.language]) {
            translationsData[trans.language] = {
              description: trans.description !== undefined && trans.description !== null 
                ? trans.description 
                : translationsData[trans.language].description || ''
            };
          }
        });
      }
      setTranslations(translationsData);
      setShowForm(true);
    }
  };

  const handleDelete = async (modelId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este modelo?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.VEHICLE_MODELS}/${modelId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchModels();
        alert('Modelo eliminado exitosamente');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Error al eliminar el modelo'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar el modelo');
    }
  };

  if (loading) {
    return (
      <div className="models-container">
        <div className="loading">Cargando modelos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="models-container">
        <div className="error-message">
          <h2>Error de Conexión</h2>
          <p>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setError(null);
              fetchModels();
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="models-container">
      <div className="models-header">
        <h1>Gestión de Modelos de Bicicletas</h1>
        <button 
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + Nuevo Modelo
        </button>
      </div>

      {showForm && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>{editingModel ? 'Editar Modelo' : 'Nuevo Modelo'}</h2>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="model-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre del Modelo *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Marca *</label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Sección de Traducciones de Descripción */}
              <div className="form-group">
                <label style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Descripción (Multiidioma)
                </label>
                <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem', backgroundColor: '#f9f9f9' }}>
                  {['es', 'en', 'de'].map((lang) => (
                    <div key={lang} style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: lang !== 'de' ? '1px solid #eee' : 'none' }}>
                      <h4 style={{ marginBottom: '0.5rem', color: '#333' }}>
                        {lang === 'es' ? 'Español' : lang === 'en' ? 'English' : 'Deutsch'}
                      </h4>
                      <div className="form-group">
                        <label>Descripción</label>
                        <textarea
                          value={translations[lang].description}
                          onChange={(e) => setTranslations({
                            ...translations,
                            [lang]: { ...translations[lang], description: e.target.value }
                          })}
                          rows="3"
                          placeholder={`Descripción en ${lang === 'es' ? 'español' : lang === 'en' ? 'inglés' : 'alemán'}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Categoría *</label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                        {category.businessGroup === 'CARRITOS' ? ' (carritos)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {!isCarritosCategory ? (
                  <div className="form-group">
                    <label>Precio Base (€/día) *</label>
                    <input
                      type="number"
                      name="basePrice"
                      value={formData.basePrice}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Precios de alquiler</label>
                    <p style={{ margin: 0, color: '#555', fontSize: '14px', lineHeight: 1.45 }}>
                      Solo por <strong>hora</strong> (franjas de 1, 2, 3 o 4 h). No hay precio por día, descuento 3–5 días ni día adicional.
                    </p>
                  </div>
                )}
              </div>

              {isCarritosCategory && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Tipo de carrito *</label>
                    <select
                      name="cartVariantType"
                      value={formData.cartVariantType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccionar tipo</option>
                      {CART_VARIANT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {isCarritosCategory && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Precio 1 hora (€) *</label>
                    <input
                      type="number"
                      name="cartPrice1h"
                      value={formData.cartPrice1h}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Precio 2 horas (€) *</label>
                    <input
                      type="number"
                      name="cartPrice2h"
                      value={formData.cartPrice2h}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>
              )}

              {isCarritosCategory && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Precio 3 horas (€) *</label>
                    <input
                      type="number"
                      name="cartPrice3h"
                      value={formData.cartPrice3h}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Precio 4 horas (€) *</label>
                    <input
                      type="number"
                      name="cartPrice4h"
                      value={formData.cartPrice4h}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>
              )}

              {!isCarritosCategory && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="models-form-checkbox">
                      <input
                        type="checkbox"
                        name="inseamOptionalEnabled"
                        checked={!!formData.inseamOptionalEnabled}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            inseamOptionalEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="models-form-checkbox__label">
                        Solicitar entrepierna (opcional) en booking
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {!isCarritosCategory && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Descuento 3-5 días (%)</label>
                    <input
                      type="number"
                      name="threeToFive"
                      value={formData.threeToFive}
                      onChange={handleInputChange}
                      step="0.1"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Precio día adicional (€)</label>
                    <input
                      type="number"
                      name="additionalDay"
                      value={formData.additionalDay}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>URLs de Imágenes</label>
                {formData.imageUrls.map((url, index) => (
                  <div key={index} className="image-url-row">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => handleImageUrlChange(index, e.target.value)}
                      placeholder="https://ejemplo.com/imagen.jpg"
                    />
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeImageUrl(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addImageUrl}
                >
                  + Añadir Imagen
                </button>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingModel ? 'Actualizar' : 'Crear'} Modelo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Buscador y controles */}
      <div className="table-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre o marca..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="pagination-controls">
          <label>
            Mostrar:
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="items-per-page-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>
          </label>
        </div>
      </div>

      <div className="models-table-container">
        {filteredModels.length === 0 ? (
          <div className="no-models">
            {searchTerm ? (
              <p>No se encontraron modelos que coincidan con "{searchTerm}"</p>
            ) : (
              <p>No hay modelos de bicicletas registrados</p>
            )}
            {!searchTerm && (
              <button 
                className="btn btn-primary"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
              >
                Crear el primer modelo
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="models-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Marca</th>
                  <th>Categoría</th>
                  <th>Carrito</th>
                  <th>Precio base / 1–4 h (€)</th>
                  <th>Descuento 3-5 días</th>
                  <th>Día Adicional</th>
                  <th>Imágenes</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentModels.map((model) => (
                  <tr key={model.id}>
                    <td>
                      <div className="model-name">
                        <strong>{model.name}</strong>
                        {model.description && (
                          <p className="model-description">{model.description}</p>
                        )}
                      </div>
                    </td>
                    <td>{model.brand}</td>
                    <td>
                      <span className={`category-badge`}>
                        {model.category.name}
                      </span>
                    </td>
                    <td>
                      {model.category?.businessGroup === 'CARRITOS' ? (
                        <span style={{ fontSize: '13px' }}>
                          {CART_VARIANT_LABELS[model.cartVariantType] || model.cartVariantType || '—'}
                        </span>
                      ) : (
                        <span className="no-image">—</span>
                      )}
                    </td>
                    <td>
                      {model.category?.businessGroup === 'CARRITOS' ? (
                        <small style={{ display: 'block', lineHeight: 1.35 }}>
                          1h: {model.cartPrice1h ?? '—'} € · 2h: {model.cartPrice2h ?? '—'} € · 3h:{' '}
                          {model.cartPrice3h ?? '—'} € · 4h: {model.cartPrice4h ?? '—'} €
                        </small>
                      ) : (
                        `€${model.basePrice}`
                      )}
                    </td>
                    <td>{model.category?.businessGroup === 'CARRITOS' ? '—' : `${model.threeToFive}%`}</td>
                    <td>{model.category?.businessGroup === 'CARRITOS' ? '—' : `€${model.additionalDay}`}</td>
                    <td>
                      {model.imageUrls.length > 0 ? (
                        <div className="image-preview">
                          <img 
                            src={model.imageUrls[0]} 
                            alt={model.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {model.imageUrls.length > 1 && (
                            <span className="image-count">+{model.imageUrls.length - 1}</span>
                          )}
                        </div>
                      ) : (
                        <span className="no-image">Sin imagen</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(model)}
                        >
                          Editar
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(model.id)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-small btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`btn btn-small ${currentPage === page ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn btn-small btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </button>
              </div>
            )}

            {/* Información de resultados */}
            <div className="results-info">
              Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredModels.length)} de {filteredModels.length} modelos
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Models;